/**
 * Utility functions for text processing and migration
 */

import DOMPurify from 'dompurify'

/**
 * Sanitizes untrusted HTML before it is injected via dangerouslySetInnerHTML.
 * Rich-text (Quill) content is authored by users and persisted, so it must be
 * cleaned on render to prevent stored XSS (e.g. <img onerror>, <script>).
 * @param html - The HTML string to sanitize
 * @returns HTML safe to render
 */
export const sanitizeHtml = (html: string): string => {
  if (!html) return ''
  return DOMPurify.sanitize(html)
}

/**
 * Converts plain text to HTML by preserving line breaks
 * @param plainText - The plain text to convert
 * @returns HTML string with line breaks converted to <br> tags
 */
export const plainTextToHtml = (plainText: string): string => {
  if (!plainText) return ''
  
  // Check if already contains HTML tags
  if (/<[^>]*>/g.test(plainText)) {
    return plainText
  }
  
  // Convert line breaks to HTML
  return plainText
    .replace(/\r\n/g, '\n') // Normalize line endings
    .replace(/\r/g, '\n')   // Handle old Mac line endings
    .replace(/\n/g, '<br>') // Convert to HTML line breaks
}

/**
 * Converts HTML to plain text by stripping tags and converting <br> to line breaks
 * @param html - The HTML string to convert
 * @returns Plain text string
 */
export const htmlToPlainText = (html: string): string => {
  if (!html) return ''
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  
  // Convert <br> tags back to line breaks
  const text = tempDiv.textContent || tempDiv.innerText || ''
  
  return text
}

/**
 * Detects if content is HTML or plain text
 * @param content - The content to check
 * @returns true if content contains HTML tags
 */
export const isHtmlContent = (content: string): boolean => {
  return /<[^>]*>/g.test(content)
}

/**
 * Safely truncates content while preserving formatting
 * @param content - The content to truncate
 * @param maxLength - Maximum length in characters
 * @returns Truncated content
 */
export const safeTruncate = (content: string, maxLength: number = 200): string => {
  if (!content) return ''
  
  if (isHtmlContent(content)) {
    // For HTML content, strip tags for length calculation
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = content
    const plainText = tempDiv.textContent || tempDiv.innerText || ''
    
    if (plainText.length <= maxLength) return content
    
    // Return truncated plain text
    return plainText.substring(0, maxLength) + '...'
  } else {
    // For plain text, truncate normally
    if (content.length <= maxLength) return content
    return content.substring(0, maxLength) + '...'
  }
}
